import React, { useEffect, useState } from "react";
import CardDiv from "modules/common/card-div/CardDiv";
import { Button } from "react-bootstrap";
import { Drawer, Table, Input, Form } from "antd";
import { useWeightageAndTarget } from "./store";
import WeightageAndTargetFilter from "./WeightageAndTargetFilter";
import { toast } from "react-toastify";

const WeightageAndTarget = () => {
  const [visible, setVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [triggerFetch, setTriggerFetch] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState([]);
  const [editedRows, setEditedRows] = useState({});
  const [queryParam, setQueryParam] = useState({
    employeeType: undefined,
    incentiveType: undefined,
  });
  const [errors, setErrors] = useState({});

  const {
    weightageAndTargetData,
    fetchWeightageAndTargetData,
    fetchWeightageAndTargetFilters,
    fetchEditWeightageAndTargetData,
  } = useWeightageAndTarget();

  const isEmpty = (value) =>
    value === undefined || value === null || value === "";
  const buildQueryString = (data) => {
    const params = new URLSearchParams();
    for (const key in data) {
      if (Array.isArray(data[key])) {
        params.append(
          key,
          `[${data[key].map((value) => `'${value}'`).join(",")}]`
        );
      } else if (data[key] !== undefined && data[key] !== "") {
        params.append(key, data[key]);
      }
    }
    return `?${params.toString()}`;
  };

  const filterAndTransformParams = (params) => {
    return Object.fromEntries(
      Object.entries(params).filter(([key, value]) =>
        Array.isArray(value) ? value.length > 0 : value
      )
    );
  };

  useEffect(() => {
    fetchWeightageAndTargetFilters();
  }, []);

  useEffect(() => {
    if (triggerFetch) {
      const updatedFilters = filterAndTransformParams(queryParam);
      const queryString = buildQueryString(updatedFilters);
      fetchWeightageAndTargetData(queryString);
    }
    setTriggerFetch(false);
  }, [triggerFetch, queryParam]);

  useEffect(() => {
    if (weightageAndTargetData.rows_data.length > 0) {
      setEditedData(weightageAndTargetData.rows_data);
    }
  }, [weightageAndTargetData.rows_data]);

  const handleApplyFilters = (filters) => {
    let updatedFilters = { ...filters };
    const mergedFilters = {
      ...queryParam,
      ...updatedFilters,
    };
    setQueryParam(mergedFilters);
    setTriggerFetch(true);
    setCurrentPage(1);
    setPageSize(10);
    setVisible(!visible);
  };

  const handleTableChange = (pagination) => {
    const updatedPagination = {
      ...queryParam,
      pageSize: pagination.pageSize,
      pageNumber: pagination.current,
    };
    setPageSize(pagination.pageSize);
    setCurrentPage(pagination.current);
    setQueryParam(updatedPagination);
    const updatedFilters = filterAndTransformParams(updatedPagination);
    setQueryParam(updatedFilters);
    setTriggerFetch(true);
  };

  const handleEditAll = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    let hasError = false;
    const updatedRows = Object.values(editedRows);
    const newErrors = {};

    for (const row of updatedRows) {
      if (isEmpty(row.weightage)) {
        newErrors[row.kpiType] = {
          ...newErrors[row.kpiType],
          weightage: "Weightage cannot be empty",
        };
        hasError = true;
      }
      if (isEmpty(row.target)) {
        newErrors[row.kpiType] = {
          ...newErrors[row.kpiType],
          target: "Target cannot be empty",
        };
        hasError = true;
      }
      const weightage = Number(row.weightage);
      const target = Number(row.target);

      if (isNaN(weightage) || weightage < 0) {
        newErrors[row.kpiType] = {
          ...newErrors[row.kpiType],
          weightage: "Weightage must be a non-negative number",
        };
        hasError = true;
      }
      if (isNaN(target) || target < 0) {
        newErrors[row.kpiType] = {
          ...newErrors[row.kpiType],
          target: "Target must be a non-negative number",
        };
        hasError = true;
      }
      if (target > 1000) {
        newErrors[row.kpiType] = {
          ...newErrors[row.kpiType],
          target: "Target cannot be more than 1000",
        };
        hasError = true;
      }
    }
    if (hasError) {
      setErrors(newErrors);
      return;
    }
    const totalWeightage = editedData.reduce(
      (sum, row) => Number(sum) + Number(row.weightage),
      0
    );

    if (totalWeightage !== 100) {
      setErrors({
        totalWeightage: "Total weightage should be exactly 100.",
      });
      toast.error("Sum of all the weightage should be exactly 100.");
      return;
    }

    const payload = {
      incentiveWeightageAndTargetId:
        weightageAndTargetData.others.incentiveWeightageAndTargetId,
      kpiWeightageAndTarget: updatedRows,
    };
    fetchEditWeightageAndTargetData(payload, queryParam);
    setIsEditing(false);
  };

  const handleClose = () => {
    setIsEditing(false);
    setEditedData(weightageAndTargetData.rows_data);
    setErrors({});
  };

  const handleInputChange = (value, key, dataIndex) => {
    const newData = editedData.map((item) => {
      if (item.kpiType === key) {
        const updatedItem = { ...item, [dataIndex]: value };
        setEditedRows((prevState) => ({
          ...prevState,
          [key]: updatedItem,
        }));
        return updatedItem;
      }
      return item;
    });
    setEditedData(newData);
  };

  const columns = [
    ...weightageAndTargetData.headers.map((header) => ({
      ...header,
      render: (text, record) => {
        if (header.dataIndex === "weightage" || header.dataIndex === "target") {
          return isEditing ? (
            <Form.Item
              validateStatus={
                errors[record.kpiType]?.[header.dataIndex] ? "error" : ""
              }
              help={errors[record.kpiType]?.[header.dataIndex]}
              style={{ margin: 0 }}
            >
              <Input
                type="text"
                value={text}
                onChange={(e) => {
                  const value = e.target.value;
                  
                  // Allow only digits and a single decimal point
                  let numericValue = value.replace(/[^0-9.]/g, "");
                
                  // Ensure there's only one decimal point
                  if (numericValue.split('.').length > 2) {
                    numericValue = numericValue.replace(/\.+$/, ""); // Remove extra decimal points
                  }
                
                  // Check if decimal part exists and limit it to 2 digits
                  if (numericValue.includes('.')) {
                    const [integerPart, decimalPart] = numericValue.split('.');
                    numericValue = decimalPart.length > 2
                      ? `${integerPart}.${decimalPart.slice(0, 2)}`
                      : numericValue;
                  }
                  // Handle errors
                  if (value !== numericValue && value !== "") {
                    setErrors((prev) => ({
                      ...prev,
                      [record.kpiType]: {
                        ...prev[record.kpiType],
                        [header.dataIndex]: "Please Enter Valid Positive Number",
                      },
                    }));
                  }
                  if (numericValue !== "" || value === "") {
                    handleInputChange(
                      value === "" ? "" : numericValue,
                      record.kpiType,
                      header.dataIndex
                    );
                    setErrors((prev) => ({
                      ...prev,
                      [record.kpiType]: {
                        ...prev[record.kpiType],
                        [header.dataIndex]: "",
                      },
                    }));
                  }
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  if (
                    value !== "" &&
                    (isNaN(Number(value)) || Number(value) < 0)
                  ) {
                    setErrors((prev) => ({
                      ...prev,
                      [record.kpiType]: {
                        ...prev[record.kpiType],
                        [header.dataIndex]:
                          "Please Enter Valid Positive Number",
                      },
                    }));
                  } else {
                    setErrors((prev) => ({
                      ...prev,
                      [record.kpiType]: {
                        ...prev[record.kpiType],
                        [header.dataIndex]: "",
                      },
                    }));
                  }
                }}
                style={{ width: 100 }}
              />
            </Form.Item>
          ) : (
            text
          );
        }
        return text;
      },
    })),
  ];

  return (
    <>
      <CardDiv
        title={"Weightage And Target Dashboard"}
        component={
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: 10,
              }}
            >
              {!isEditing && (
                <Button
                  type="primary"
                  onClick={() => setVisible(true)}
                  shape="round"
                  style={{ marginRight: 10 }}
                >
                  Filter
                </Button>
              )}
              {isEditing ? (
                <>
                  <Button
                    type="primary"
                    onClick={handleSave}
                    shape="round"
                    style={{ marginRight: 10 }}
                  >
                    Save
                  </Button>
                  <Button
                    type="secondary"
                    onClick={handleClose}
                    shape="round"
                    style={{ marginRight: 10 }}
                  >
                    Close
                  </Button>
                </>
              ) : (
                <Button
                  type="primary"
                  onClick={handleEditAll}
                  shape="round"
                  style={{ marginRight: 10 }}
                >
                  Edit All
                </Button>
              )}
              <Drawer
                title="Filter for Weightage And Target"
                placement="right"
                onClose={() => setVisible(false)}
                visible={visible}
                width="30%"
                zIndex={999999999}
              >
                <WeightageAndTargetFilter
                  onApplyFilters={handleApplyFilters}
                  onClose={() => setVisible(!visible)}
                  setQueryParam={setQueryParam}
                />
              </Drawer>
            </div>

            <div className="table-responsive">
              <Table
                columns={columns}
                dataSource={editedData}
                onChange={handleTableChange}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: weightageAndTargetData.total_row_count,
                  showSizeChanger: true,
                }}
                scroll={{ x: "max-content" }}
              />
            </div>
          </>
        }
      />
    </>
  );
};

export default WeightageAndTarget;
